// src/api/api.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../config/constants";
import criticalLogger from "../utils/criticalLogger";

// Endpoints that should NOT be logged (too noisy / expected failures)
const SKIP_LOG_ENDPOINTS = [
  "/logs/frontend", // prevent infinite logging loop
  "/auth/refresh",  // handled by token refresh logic separately
];
function shouldSkipLog(url = "") {
  return SKIP_LOG_ENDPOINTS.some((skip) => url.includes(skip));
}
// Endpoints where a specific status is an expected/benign response and should not be
// logged. Only that status is suppressed — other failures (e.g. a genuine 500) still log.
const SKIP_LOG_ON_STATUS = [
  { match: "/payment/session-by-order/", status: 404 }, // benign race, retried in CheckoutRedirectCallback
];
function shouldSkipLogForStatus(url = "", status) {
  return SKIP_LOG_ON_STATUS.some((rule) => rule.status === status && url.includes(rule.match));
}

// Keys redacted (case-insensitively, by substring) from request bodies before they're
// sent to error logging/Sentry — request bodies on this app can carry customer PII and
// payment references, so raw payloads must never leave the browser as-is.
const SENSITIVE_KEYS = ["password", "token", "otp", "cvv", "card", "secret", "authorization"];
function redactSensitiveKeys(value, depth = 0) {
  if (depth > 3 || value === null || typeof value !== "object" || Array.isArray(value)) return value;
  const result = {};
  for (const [key, val] of Object.entries(value)) {
    if (SENSITIVE_KEYS.some((k) => key.toLowerCase().includes(k))) {
      result[key] = "[REDACTED]";
    } else {
      result[key] = redactSensitiveKeys(val, depth + 1);
    }
  }
  return result;
}
function redactRequestBody(data) {
  if (!data) return undefined;
  if (typeof FormData !== "undefined" && data instanceof FormData) return "[FormData]";
  let parsed = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return data;
    }
  }
  return redactSensitiveKeys(parsed);
}

// Attaches an error-logging interceptor to any axios instance
function attachErrorLogger(instance, label) {
  instance.interceptors.response.use(
    (res) => res,
    (err) => {
      const url = err.config?.url || "unknown";
      const status = err.response?.status;
      // Skip 401 (token refresh handles it), noisy endpoints, expected-status responses,
      // and cancelled requests
      if (status !== 401 && !shouldSkipLog(url) && !shouldSkipLogForStatus(url, status) && !axios.isCancel(err)) {
        criticalLogger.apiError(`[${label}] ${url}`, err, {
          status_code: status,
          method: err.config?.method?.toUpperCase(),
          response_data: err.response?.data,
          request_body: redactRequestBody(err.config?.data),
          page: window.location.pathname,
        });
      }
      return Promise.reject(err);
    }
  );
}

let currentRole = "admin"; // default, or set dynamically


export function setApiRole(role) {
  currentRole = role;
}

// Use centralized API base URL from constants
const BASE_URL = API_BASE_URL; 

// Auth API (public endpoints, no token)
export const authApi = axios.create({
  baseURL: BASE_URL + "/auth",
  timeout: 30000, // Optional timeout
  headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
});
export const publicApi = axios.create({
  baseURL: BASE_URL + "/public",
  timeout: 30000, // Optional timeout
  headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
});

// Attach admin/user token to publicApi when available (for admin-protected public routes)
publicApi.interceptors.request.use(
  (config) => {
    const adminToken = localStorage.getItem("adminAccessToken");
    const userToken = localStorage.getItem("userAccessToken");
    const token = adminToken || userToken;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);
export const rootApi = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
});

// Protected API (token attached)
export const api = axios.create({
  baseURL: BASE_URL + "/api",
  timeout: 30000, // Optional timeout
    headers: {
    "ngrok-skip-browser-warning": "true"
  }
});

// Attach error logging to all instances (catches errors in every page automatically)
attachErrorLogger(authApi, "authApi");
attachErrorLogger(publicApi, "publicApi");
attachErrorLogger(rootApi, "rootApi");
attachErrorLogger(api, "api");

// === Interceptor to attach token ===
api.interceptors.request.use(
  (config) => {
    let token;
    if (currentRole === "admin") {
      token = localStorage.getItem("adminAccessToken");
    } else if (currentRole === "user") {
      token = localStorage.getItem("userAccessToken");
    }
    // Attach token if present (do not check expiry here)
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    // Only set Content-Type for non-FormData requests
    if (
      config.data &&
      typeof config.data === "object" &&
      !(config.data instanceof FormData)
    ) {
      config.headers["Content-Type"] = "application/json";
    } else {
      // Let browser/axios set Content-Type for FormData (file upload)
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// === Optional: Interceptor to handle 401 errors globally ===
api.interceptors.response.use(
 (res) => {
    // ✅ If PDF or binary, return raw response
    const contentType = res.headers["content-type"];
    if (contentType && (contentType.includes("application/pdf") || contentType.includes("octet-stream"))) {
      return res;
    }
    // Normal JSON APIs
    return res;
  },
  async (err) => {
    if (err.response && err.response.status === 401) {
      let refreshToken = "";
      if (currentRole === "admin") {
        refreshToken = localStorage.getItem("adminRefreshToken");
      } else if (currentRole === "user") {
        refreshToken = localStorage.getItem("userRefreshToken");
      }
      // Idle check
      const IDLE_LIMIT_SECONDS = 900;
      const lastActivity = parseInt(localStorage.getItem("lastActivity"), 10);
      const nowMs = Date.now();
      const idleSeconds = lastActivity ? (nowMs - lastActivity) / 1000 : 0;
      if (!refreshToken || idleSeconds > IDLE_LIMIT_SECONDS) {
        // Redirect to login if no refresh token or idle too long
        if (currentRole === "admin") {
          localStorage.removeItem("adminAccessToken");
          localStorage.removeItem("adminRefreshToken");
          localStorage.removeItem("adminInfo");
          window.location.href = "/admin/";
        } else {
          localStorage.removeItem("userAccessToken");
          localStorage.removeItem("userRefreshToken");
          localStorage.removeItem("user");
          window.location.href = "/consultant";
        }
        return Promise.reject(err);
      }

      // Try refreshing - FIXED: Correct endpoint path
      try {
        const { data } = await authApi.post("/auth/refresh", {
          refreshToken,
        });

        if (currentRole === "admin") {
          // Store new tokens
          localStorage.setItem("adminAccessToken", data.accessToken);
          localStorage.setItem("adminRefreshToken", data.refreshToken);
        } else if (currentRole === "user") {
          localStorage.setItem("userAccessToken", data.accessToken);
          localStorage.setItem("userRefreshToken", data.refreshToken); // FIXED: Typo correction
        }

        // Update activity timestamp
        localStorage.setItem("lastActivity", Date.now().toString());

        // Retry original request
        err.config.headers.Authorization = `Bearer ${data.accessToken}`;
        return api.request(err.config);
      } catch (refreshErr) {
        console.error("Refresh failed:", refreshErr);

        if (currentRole === "admin") {
          localStorage.removeItem("adminAccessToken");
          localStorage.removeItem("adminRefreshToken");
          localStorage.removeItem("adminInfo");

          window.location.href = "/admin/";
        } else {
          localStorage.removeItem("userAccessToken");
          localStorage.removeItem("userRefreshToken");
          localStorage.removeItem("user");
          window.location.href = "/consultant";
        }
      }
    }
    return Promise.reject(err);
  }
);

// === Generic Methods ===
const get = (url, params = {}) => api.get(url, { params });
const post = (url, data) => api.post(url, data);
const put = (url, data) => api.put(url, data);
const patch = (url, data) => api.patch(url, data);
const del = (url, data = {}) => api.delete(url, { data });
const download = (url, params = {}) =>
  api.get(url, {
    params,
    responseType: "arraybuffer",
    transformResponse: [(data) => data],
  });

// === Public Config Utility ===
export const getPublicKey = async (keyName) => {
  try {
    const response = await publicApi.get(`/config?key=${keyName}`);
    if (response.data.status === 'success') {
      return response.data.data.value;
    }
    throw new Error(`Failed to fetch ${keyName}`);
  } catch (error) {
    console.error(`Error fetching ${keyName}:`, error);
    throw error;
  }
};

// === Meta Conversions API Utility ===
export const sendMetaConversionsEvent = async (eventData) => {
  try {
    const response = await api.post('/orders/meta-conversions', eventData);
    return response.data;
  } catch (error) {
    console.error('Meta Conversions API error:', error);
    throw error;
  }
};

export default {
  get,
  post,
  put,
  patch,
  delete: del,
  download,
};
